import api from '@/apis'

// Backend Schema.V1 declares parameters as dict[str, str] — Pydantic v1 rejects
// `null` values with HTTP 422 before our render() ever sees them. Prompt-library
// optional fields are pre-seeded as null in the store so v-model writes stay
// reactive, so coerce nullish values to '' on the wire. PromptDoc.render then
// applies the optional-as-empty rule correctly.
function _coerceParamsForApi(params) {
  if (!params) return {}
  const out = {}
  for (const k of Object.keys(params)) {
    const v = params[k]
    out[k] = v === null || v === undefined ? '' : String(v)
  }
  return out
}

export default {
  state: {
    current_step: 1,
    sent_verification_code: false,
    tab_list: [],
    btn_disable: false,
    step_1: {
      name: null,
      company_name: null,
      company_email: null,
      country_code: null,
      phone_number: null,
      verification_code: null,
      details: { request_id: 1 },
      session_token: null
    },
    step_2: {
      scenario: {},
      scenario_id: 1,
      parameters: { language: null }
    },
    // Country picker — populated from GET /enabled-country-codes (SDD §6.7).
    // Fallback to the pre-SDD literal triple if the call fails so the picker
    // never goes blank from a network blip.
    enabled_country_codes: ['HK', 'TW', 'SG'],
    enabled_country_codes_status: 'pending',
    // Captured from vue-tel-input @country-changed.iso2 — sent with
    // registerDemoRequest so the backend can enforce ISO2-keyed
    // country-code gating (SDD §6.7.1).
    step_1_country_iso2: null
  },
  mutations: {
    SET_CURRENT_STEP(state, payload) {
      state.current_step = payload
    },
    SET_BTN_DISABLE(state, payload) {
      state.btn_disable = payload
    },

    // step 1
    SET_SENT_VERIFICATION_CODE(state, payload) {
      state.sent_verification_code = payload
    },
    SET_STEP_1_VERIFICATION_CODE(state, payload) {
      state.step_1.verification_code = payload
    },
    SET_STEP_1_DETAILS(state, payload) {
      state.step_1.details = payload
    },
    SET_STEP_1_SESSION_TOKEN(state, payload) {
      state.step_1.session_token = payload
    },
    SET_COUNTRY_CODE(state, payload) {
      state.step_1.country_code = payload
    },
    SET_COUNTRY_ISO2(state, payload) {
      state.step_1_country_iso2 = (payload || null) && String(payload).toUpperCase()
    },
    SET_ENABLED_COUNTRY_CODES(state, { list, status }) {
      if (Array.isArray(list) && list.length) state.enabled_country_codes = list
      state.enabled_country_codes_status = status || 'ok'
    },

    // step 2
    SET_STEP_2_SCENARIO(state, payload) {
      state.step_2.scenario = payload
      state.tab_list = payload.title
    },
    SET_STEP_2_SCENARIO_ID(state, payload) {
      state.step_2.scenario_id = payload
    },
    RESET_STEP_2_PARAMETERS(state, parameters) {
      // Pre-seed every parameter key as null so v-model writes from Step 2 stay
      // reactive — Vue 2 doesn't track new keys added to an existing object.
      // Key resolution: legacy DB rows have shape {name: <display label>,
      // value: <real key>} → use `value`. Prompt-library rows have shape
      // {name: <variable id>, type, required, ...} (no `value`) → use `name`.
      const next = { language: null }
      for (const p of parameters || []) next[p.value || p.name] = null
      state.step_2.parameters = next
    },
    RESEED_STEP_2_REACTIVE_KEYS(state, parameters) {
      // Merge-style version of RESET — adds any missing keys (so v-model
      // writes are reactive) without nuking `language` or values the user
      // already typed. Used after the user picks a language, since the
      // effective prompt-library variable list only resolves at that point.
      if (!parameters || !state.step_2.parameters) return
      const merged = { ...state.step_2.parameters }
      let changed = false
      for (const p of parameters) {
        const key = p.value || p.name
        if (key && !(key in merged)) {
          merged[key] = null
          changed = true
        }
      }
      if (changed) state.step_2.parameters = merged
    },
    SET_STEP_2_PARAMETERS_LANGUAGE(state, payload) {
      state.step_2.parameters.language = payload
    }
  },
  actions: {
    async fetchEnabledCountryCodes({ commit }) {
      try {
        const response = await api.getEnabledCountryCodes()
        const body = response.data || {}
        if (body.success && Array.isArray(body.data)) {
          commit('SET_ENABLED_COUNTRY_CODES', {
            list: body.data,
            status: body.data.length ? 'ok' : 'empty'
          })
        } else {
          commit('SET_ENABLED_COUNTRY_CODES', { list: null, status: 'error' })
        }
      } catch (_e) {
        // Keep the hardcoded fallback list; surface degraded state so
        // the UI can show a "using defaults" hint if it wants.
        commit('SET_ENABLED_COUNTRY_CODES', { list: null, status: 'error' })
      }
    },
    async registerDemoRequest({ commit, state }) {
      try {
        commit('SET_BTN_DISABLE', true)
        commit('SET_SNACKBAR', { status: true, color: 'loading', text: 'Sending verification code', icon: 'check', loading: true })
        const params = {
          name: state.step_1.name,
          company_name: state.step_1.company_name,
          company_email: state.step_1.company_email,
          country_code: state.step_1.country_code,
          country_iso2: state.step_1_country_iso2 || undefined,
          phone_number: state.step_1.phone_number
        }
        const response = await api.createDemoRequest({ params })
        if (response.data.success) {
          commit('SET_SENT_VERIFICATION_CODE', true)
          commit('SET_STEP_1_DETAILS', response.data.data)
          commit('SET_SNACKBAR', { status: true, color: 'success', text: 'Verification code sent', icon: 'check', timeout: '2000' })
        } else throw Error(response.data.message)
      } catch (error) {
        commit('SET_SNACKBAR', { status: true, color: 'error', text: error.message, icon: 'close', timeout: '2000' })
      } finally {
        commit('SET_BTN_DISABLE', false)
      }
    },
    async submitVerificationCode({ state, commit, dispatch }) {
      try {
        commit('SET_BTN_DISABLE', true)
        commit('SET_SNACKBAR', { status: true, color: 'loading', text: 'Verifying...', loading: true })
        const response = await api.verifyOtpSms({ params: { request_id: state.step_1.details.request_id, otp: state.step_1.verification_code } })
        if (response.data.success) {
          commit('SET_STEP_1_SESSION_TOKEN', response.data.data.session_token)
          commit('SET_SNACKBAR', { status: true, color: 'success', text: 'Verification success', icon: 'check', timeout: '2000' })
          commit('SET_CURRENT_STEP', 2)
          dispatch('fetchSimulatedScenario')
        } else throw Error(response.data.message)
      } catch (error) {
        commit('SET_SNACKBAR', { status: true, color: 'error', text: error.message, icon: 'close', timeout: '2000' })
      } finally {
        commit('SET_STEP_1_VERIFICATION_CODE', null)
        commit('SET_BTN_DISABLE', false)
      }
    },
    async fetchSimulatedScenario({ commit }) {
      try {
        commit('SET_SNACKBAR', { status: true, color: 'loading', text: 'Loading Scenario...', loading: true })
        const response = await api.getSimulatedScenario()
        if (response.data.success) {
          commit('SET_STEP_2_SCENARIO', response.data.data)
          commit('SET_SNACKBAR', { status: true, color: 'success', text: 'Scenario loaded', icon: 'check', timeout: '2000' })
        } else throw Error(response.data.message)
      } catch (error) {
        commit('SET_SNACKBAR', { status: true, color: 'error', text: error.message, icon: 'close', timeout: '2000' })
      }
    },
    async makeCall({ state, commit }) {
      try {
        commit('SET_BTN_DISABLE', true)
        commit('INIT_LOAD_CALL_DIALOG')
        const response = await api.createSimulatedScenario({
          params: {
            request_id: state.step_1.details.request_id,
            scenario_id: state.step_2.scenario_id,
            parameters: _coerceParamsForApi(state.step_2.parameters),
            session_token: state.step_1.session_token
          }
        })
        if (response.data.success) {
          setTimeout(() => {
            commit('RESET_LOAD_CALL_LOADING', { success: 'success' })
          }, 3000)
        } else throw Error(response.data.message)
      } catch (error) {
        setTimeout(() => {
          commit('RESET_LOAD_CALL_LOADING', { success: 'error', message: error.message })
        }, 2000)
        // commit('SET_SNACKBAR', { status: true, color: 'error', text: error.message, icon: 'close', timeout: '2000' })
      } finally {
        commit('SET_BTN_DISABLE', false)
      }
    },
    async previewScript({ state, commit }) {
      try {
        commit('SET_BTN_DISABLE', true)
        commit('SET_SNACKBAR', { status: true, color: 'loading', text: 'Loading script...', loading: true })
        const response = await api.createScriptPreview({ params: { scenario_id: state.step_2.scenario_id, parameters: _coerceParamsForApi(state.step_2.parameters) } })
        if (response.data.success) {
          commit('INIT_PREVIEW_SCRIPT_DIALOG', response.data.data.script)
          commit('SET_SNACKBAR', { status: true, color: 'success', text: 'Script loaded', icon: 'check', timeout: '2000' })
        } else throw Error(response.data.message)
      } catch (error) {
        commit('SET_SNACKBAR', { status: true, color: 'error', text: error.message, icon: 'close', timeout: '2000' })
      } finally {
        commit('SET_BTN_DISABLE', false)
      }
    }
  }
}
